// One register item (V0.3.0 layout): type identity, status and stage in one click, who the onus is on, responses &
// revisions, follow-ups, related emails, files with From/Note, comments in a right-hand rail, and the full history.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { useLive } from '../lib/live';
import { usePresence } from '../lib/presence';
import { write } from '../lib/api';
import { fmtDate, fmtDateTime, fmtMoney } from '../lib/format';
import { isClosed, isEot, itemFields, registerFor, showValue, stageIndex, stagesFor, statusLabel, TYPE_LOOK, type Item } from '../lib/registers';
import { Button, Card, Empty, errText, useToast } from '../ui/kit';
import { RecordDrawer } from '../ui/RecordDrawer';
import { Comments } from '../ui/Comments';
import { ItemFiles, type FileRow } from '../ui/ItemFiles';
import { TypeTag, TypeTile } from '../ui/TypeTag';
import { BackAndForth, OnusBar, StageTracker, StatusMenu, stageMode, type Response } from '../ui/item/Workflow';
import { Responses, type ResponsesHandle } from '../ui/item/Responses';
import { FollowUps, type FollowUp } from '../ui/item/FollowUps';
import { Emails, type ItemEmail } from '../ui/item/Emails';
import { ReviewBanner } from '../ui/item/ReviewBanner';
import { History } from '../ui/item/History';
import { Facts, type FactSpec } from '../ui/item/Facts';
import { ClaimSection } from '../ui/claim/ClaimSection';
import { daysSince, dueText, plural, type Named, type Org, type Person } from '../ui/item/shared';

const NONE = '00000000-0000-0000-0000-000000000000';
const INTERNAL_ROLES = new Set(['super_admin', 'pm', 'field', 'swi_finance']);

/** The stage a final response moves the item to (null = no shortcut for this register). */
function finalStage(item: Item): { key: string; label: string } | null {
  const want = item.type === 'rfi' ? 'answered' : item.type === 'notice' ? (isEot(item) ? 'determined' : 'responded') : null;
  const s = want ? stagesFor(item).find((x) => x.key === want) : undefined;
  return s ? { key: s.key, label: s.label } : null;
}

export default function ItemPage() {
  const { module, id } = useParams();
  const def = registerFor(module);
  const { siteId, can, access, session } = useApp();
  const { setRecord, editing } = usePresence();
  const toast = useToast();
  const [item, setItem] = useState<Item | null>(null);
  const [missing, setMissing] = useState(false);
  const [edit, setEdit] = useState(false);
  const [tab, setTab] = useState<'details' | 'comments'>('details');
  const [nComments, setNComments] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  const resp = useRef<ResponsesHandle>(null);
  const fileBox = useRef<HTMLDivElement>(null);
  const fuBox = useRef<HTMLDivElement>(null);
  const mailBox = useRef<HTMLDivElement>(null);
  const me = session?.user.id;
  const internal = Boolean(access?.super_admin || access?.roles.some((r) => INTERNAL_ROLES.has(r.role)));

  const load = useCallback(async () => {
    const { data } = await supabase.from('items').select('*').eq('id', id ?? NONE).maybeSingle();
    if (data) setItem(data as Item);
    else setMissing(true);
  }, [id]);
  useEffect(() => {
    setItem(null);
    setMissing(false);
    setTab('details');
    void load();
  }, [load]);
  useLive(['items'], () => void load(), { filter: `id=eq.${id}`, enabled: Boolean(id) });

  useEffect(() => {
    if (!id) return;
    setRecord(`items:${id}`);
    return () => setRecord(null);
  }, [id, setRecord]);

  const files = useRows<FileRow>(
    () =>
      supabase
        .from('files')
        .select('id, original_name, mime, size_bytes, kind, external_url, doc_key, version, is_current, comment_id, uploaded_at, uploaded_by_name, source, from_org_id, notes, in_pack, created_at, deleted_at')
        .eq('linked_table', 'items')
        .eq('linked_id', id ?? NONE)
        .is('deleted_at', null),
    [id],
    ['files'],
    `linked_id=eq.${id}`,
  );
  const responses = useRows<Response>(
    () => supabase.from('item_responses').select('*').eq('item_id', id ?? NONE).is('deleted_at', null).order('created_at'),
    [id],
    ['item_responses'],
    `item_id=eq.${id}`,
  );
  const followUps = useRows<FollowUp>(
    () => supabase.from('follow_ups').select('*').eq('item_id', id ?? NONE).is('deleted_at', null),
    [id],
    ['follow_ups'],
    `item_id=eq.${id}`,
  );
  const emails = useRows<ItemEmail>(
    () => supabase.from('item_emails').select('*').eq('item_id', id ?? NONE).is('deleted_at', null),
    [id],
    ['item_emails'],
    `item_id=eq.${id}`,
  );
  const buildings = useRows<Named>(
    () => supabase.from('buildings').select('id, name, short_code, aliases').eq('site_id', item?.site_id ?? siteId ?? NONE).is('deleted_at', null).order('sort').order('name'),
    [item?.site_id, siteId],
    ['buildings'],
  );
  const orgs = useRows<Org>(() => supabase.from('organisations').select('id, name, short_code, aliases, kind').is('deleted_at', null).order('name'), [], ['organisations']);
  const people = useRows<Person>(() => supabase.from('profiles').select('id, full_name, email').is('deleted_at', null).order('full_name'), []);
  const staff = useRows<{ user_id: string }>(
    () => supabase.from('user_roles').select('user_id, role_key').in('role_key', [...INTERNAL_ROLES]).is('deleted_at', null),
    [],
  );

  const bMap = useMemo(() => new Map(buildings.rows.map((b) => [b.id, b])), [buildings.rows]);
  const oMap = useMemo(() => new Map(orgs.rows.map((o) => [o.id, o])), [orgs.rows]);
  const pMap = useMemo(() => new Map(people.rows.map((p) => [p.id, p])), [people.rows]);
  const staffList = useMemo(() => {
    const ids = new Set(staff.rows.map((s) => s.user_id));
    return people.rows.filter((p) => ids.has(p.id));
  }, [staff.rows, people.rows]);
  const swiId = orgs.rows.find((o) => o.short_code === 'SWI')?.id;
  const pbsId = orgs.rows.find((o) => o.short_code === 'PBS')?.id;

  if (!def) return <Navigate to="/" replace />;
  if (missing) return <Empty>This item was not found, or you do not have access to it.</Empty>;
  if (!item) return <Empty>Loading…</Empty>;

  const look = TYPE_LOOK[item.type];
  const editable = can(def.module, 4) && !item.deleted_at;
  const canComment = can(def.module, 2) && !item.deleted_at;
  const others = editing('items', item.id);
  const st = stagesFor(item);
  const at = stageIndex(item);
  const cur = st[at];
  const fin = finalStage(item);
  const rounds = responses.rows.length;

  async function update(values: Record<string, unknown>, summary: string, batch?: string): Promise<boolean> {
    try {
      await write('items', 'update', { id: item!.id, batch_id: batch, values: { ...values, _expect_updated_at: item!.updated_at }, summary });
      await load();
      return true;
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
      void load();
      return false;
    }
  }
  const pickStage = (key: string) => {
    const s = st.find((x) => x.key === key)!;
    void update({ stage: key, status: s.status }, `Stage → ${s.label}`);
  };
  const pickStatus = (status: string) => {
    // keep the stage when it already fits; hold / void / rejected leave the stage where it was
    let stage: string | null = item.stage ?? cur.key;
    if (cur.status !== status) {
      const match = [...st].reverse().find((x) => x.status === status);
      if (match) stage = match.key;
    }
    void update({ status, stage }, `Status → ${statusLabel(status, item.type)}`);
  };
  const pickOnus = (org: string | null) => void update({ onus_org_id: org }, org ? `Onus → ${oMap.get(org)?.name ?? ''}` : 'Onus cleared');

  // the main button follows the stage
  const next = st[at + 1];
  const atEnd = at === st.length - 1;
  let mainLabel: string | null = null;
  let mainRun: (() => void) | null = null;
  if (item.status === 'hold') {
    mainLabel = 'Take off hold';
    mainRun = () => void update({ status: cur.status, stage: cur.key }, 'Taken off hold');
  } else if (atEnd && isClosed(item.status)) {
    mainLabel = 'Reopen';
    mainRun = () => pickStage(st[Math.max(0, st.length - 2)].key);
  } else if (item.type === 'claim' && ['swi_review', 'architect_review', 'certified', 'client_approval'].includes(cur.key)) {
    // claims: signing, certifying and paying happen in Approval & signing (never by just moving the stage)
    mainLabel = { swi_review: 'Review & sign for SWI', architect_review: 'Certify / record the architect’s signature', certified: 'Send to client finance', client_approval: 'Mark paid' }[cur.key]!;
    mainRun = () => document.getElementById('claim-approval')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } else if (cur.action && next) {
    mainLabel = cur.action;
    if (/^Record /.test(cur.action) && fin) mainRun = () => resp.current?.open(true);
    else if (item.type === 'rfi' && cur.key === 'raised' && item.directed_to[0])
      mainRun = () => void update({ stage: next.key, status: next.status, onus_org_id: item.directed_to[0] }, `Stage → ${next.label}`);
    else mainRun = () => pickStage(next.key);
  }

  async function afterResponse({ final, batch }: { final: boolean; batch: string }) {
    if (final && fin) {
      const s = st.find((x) => x.key === fin.key)!;
      await update({ stage: s.key, status: s.status }, `Stage → ${s.label} (final response)`, batch);
    } else await load();
    void responses.reload();
  }

  // ---------- facts (click to edit) ----------
  const late = item.respond_by && !isClosed(item.status) ? dueText(item.respond_by) : null;
  const chips = (ids: string[], map: Map<string, { name: string }>, raw: string[]) => {
    const linked = ids.map((x) => map.get(x)?.name).filter(Boolean) as string[];
    const list = linked.length ? linked : raw;
    return list.length ? (
      <span className="chips">
        {list.map((n) => (
          <span key={n} className="fchip">
            {n}
          </span>
        ))}
      </span>
    ) : (
      <span className="muted">—</span>
    );
  };
  const bOpts = buildings.rows.map((b) => ({ value: b.id, label: b.name }));
  const oOpts = orgs.rows.map((o) => ({ value: o.id, label: o.name }));
  const facts: FactSpec[] = [];
  if (def.subtypes) facts.push({ key: 'subtype', label: 'Type', type: 'select', value: item.subtype, display: item.subtype ?? '—', options: def.subtypes.map((s) => ({ value: s, label: s })) });
  if (item.type !== 'nda')
    facts.push({ key: 'ref_no', label: item.type === 'claim' ? 'Claim #' : item.type === 'variation' ? 'VAR ref #' : 'PBS CTC #', type: 'text', value: item.ref_no, display: item.ref_no ?? '—' });
  if (item.type !== 'nda') facts.push({ key: 'building_ids', label: 'Buildings', type: 'multi', value: item.building_ids, display: chips(item.building_ids, bMap, item.buildings_raw), options: bOpts });
  if (def.hasOrg)
    facts.push({ key: 'org_id', label: 'Contractor', type: 'select', value: item.org_id, display: (item.org_id && oMap.get(item.org_id)?.name) || item.org_raw || '—', options: oOpts });
  if (item.type !== 'nda') facts.push({ key: 'directed_to', label: 'Directed to', type: 'multi', value: item.directed_to, display: chips(item.directed_to, oMap, item.directed_raw), options: oOpts });
  if (def.money) facts.push({ key: 'amount', label: `Amount (${def.money})`, type: 'number', value: item.amount, display: fmtMoney(item.amount) });
  if (item.type !== 'nda') facts.push({ key: 'submitted_on', label: 'Submitted', type: 'date', value: item.submitted_on, display: fmtDate(item.submitted_on) });
  if (def.hasRespondBy)
    facts.push({
      key: 'respond_by',
      label: 'Respond by',
      type: 'date',
      value: item.respond_by,
      display: fmtDate(item.respond_by),
      extra: late ? <span className={`due due-${late.tone}`}>{late.text}</span> : null,
    });
  if (isEot(item)) {
    facts.push({ key: 'eot_days_claimed', label: 'EOT days claimed', type: 'number', value: item.eot_days_claimed, display: item.eot_days_claimed ?? '—' });
    facts.push({ key: 'eot_days_granted', label: 'EOT days granted', type: 'number', value: item.eot_days_granted, display: item.eot_days_granted ?? '—' });
  }
  const data = item.data ?? {};
  const keyData = (def.keyData ?? []).filter((k) => data[k] !== undefined);
  for (const k of keyData) facts.push({ key: `data:${k}`, label: k, type: 'readonly', value: data[k], display: showValue(data[k]) });
  facts.push({ key: 'notes', label: 'Comments / notes', type: 'textarea', value: item.notes, display: item.notes || <span className="muted">—</span>, wide: true });
  if (item.type !== 'nda') facts.push({ key: 'remark', label: item.type === 'variation' ? 'Hold remarks' : 'Remark / directive', type: 'textarea', value: item.remark, display: item.remark || <span className="muted">—</span>, wide: true });
  if ((item.type === 'rfi' || item.type === 'notice') && (item.response || editable))
    facts.push({ key: 'response', label: 'Response (summary)', type: 'textarea', value: item.response, display: item.response || <span className="muted">—</span>, wide: true });
  const restData = Object.entries(data).filter(([k]) => !keyData.includes(k));

  // names for the history log
  const names = (k: string, v: unknown): string | null => {
    if (typeof v !== 'string') return null;
    if (k === 'building_ids') return bMap.get(v)?.name ?? null;
    if (['directed_to', 'org_id', 'onus_org_id', 'from_org_id'].includes(k)) return oMap.get(v)?.name ?? null;
    if (['assignee_id', 'done_by'].includes(k)) return pMap.get(v)?.full_name ?? null;
    if (k === 'status') return statusLabel(v, item.type);
    if (k === 'stage') return st.find((s) => s.key === v)?.label ?? null;
    return null;
  };
  const version = [item.updated_at, files.rows.length, responses.rows.length, followUps.rows.map((f) => f.done_at).join(), emails.rows.length, nComments].join('|');

  const onus = item.onus_org_id ? oMap.get(item.onus_org_id) : undefined;
  const mode = stageMode(item, swiId);
  const stageNote =
    mode === 'fin'
      ? 'Concluded'
      : mode === 'hold'
        ? 'On hold'
        : mode === 'rej'
          ? statusLabel(item.status, item.type)
          : onus
            ? onus.id === swiId
              ? 'Onus is on SWI — our move'
              : `Waiting on ${onus.name}${daysSince(item.onus_since) !== null ? ` · ${plural(daysSince(item.onus_since)!, 'day')}` : ''}`
            : editable
              ? 'Click a stage to move it'
              : '';
  const scrollTo = (r: React.RefObject<HTMLDivElement | null>) => r.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  return (
    <div className="item-page" style={{ ['--type' as string]: look.color, ['--type-tint' as string]: look.tint, ['--type-line' as string]: look.line }}>
      <div className="type-band" />
      <div className="item-tabs" role="tablist" aria-label="Item sections">
        <button type="button" role="tab" aria-selected={tab === 'details'} className={tab === 'details' ? 'on' : ''} onClick={() => setTab('details')}>
          Details
        </button>
        <button type="button" role="tab" aria-selected={tab === 'comments'} className={tab === 'comments' ? 'on' : ''} onClick={() => setTab('comments')}>
          Comments{nComments ? ` · ${nComments}` : ''}
        </button>
      </div>
      <div className={`item-grid show-${tab}`}>
        <div className="item-main">
          <header className="item-hero">
            <Link className="back" to={`/r/${def.module}`}>
              ← {def.label}
            </Link>
            <div className="hero-row">
              <TypeTile type={item.type} />
              <div className="hero-text">
                <div className="hero-kicker">
                  <span className="kicker">{item.subtype || look.name}</span>
                  {item.building_ids.map((b) => (bMap.get(b) ? <span key={b} className="fchip white">{bMap.get(b)!.name}</span> : null))}
                  {item.deleted_at ? <span className="fchip dark">Deleted</span> : null}
                </div>
                <h1 className="hero-num">{item.number ?? def.one}</h1>
                <p className="hero-title">{item.title}</p>
              </div>
              <StatusMenu item={item} def={def} canEdit={editable} onPick={pickStatus} />
            </div>
            {others.length ? (
              <div className="banner banner-info" role="status">
                {others.map((o) => o.name).join(', ')} {others.length === 1 ? 'is' : 'are'} looking at this item too.
              </div>
            ) : null}
          </header>

          <ReviewBanner item={item} def={def} buildings={buildings.rows} orgs={orgs.rows} canEdit={editable} onChanged={() => void load()} />

          {(
            <section className="card stage-card">
              <div className="stage-head">
                <span className="k" style={{ color: look.color }}>
                  Where this is at
                </span>
                <span className="muted small">{stageNote}</span>
              </div>
              <StageTracker item={item} orgs={oMap} swiId={swiId} canEdit={editable} rounds={rounds} onPick={pickStage} />
              {item.type !== 'nda' ? (
                <>
                  <OnusBar item={item} orgs={oMap} allOrgs={orgs.rows} swiId={swiId} pbsId={pbsId} canEdit={editable} onPick={pickOnus} />
                  <BackAndForth item={item} responses={responses.rows} orgs={oMap} />
                </>
              ) : null}
            </section>
          )}

          <div className="item-actions">
            {editable && mainLabel && mainRun ? (
              <Button kind="primary" onClick={mainRun}>
                → {mainLabel}
              </Button>
            ) : null}
            {editable && item.type !== 'nda' ? <Button onClick={() => resp.current?.open(false)}>Add response</Button> : null}
            {canComment ? <Button onClick={() => scrollTo(fuBox)}>Add follow-up</Button> : null}
            {internal && editable ? <Button onClick={() => scrollTo(mailBox)}>Link email</Button> : null}
            {editable ? <Button onClick={() => scrollTo(fileBox)}>Upload file</Button> : null}
            {editable ? (
              <Button kind="ghost" onClick={() => setEdit(true)}>
                Edit all fields
              </Button>
            ) : null}
            <span className="grow" />
            {editable ? <span className="muted small">Click any field to edit it</span> : null}
          </div>

          {item.type === 'claim' ? <ClaimSection item={item} files={files.rows} canEdit={editable} canComment={canComment} onFiles={() => void files.reload()} /> : null}

          <Card>
            <Facts facts={facts} canEdit={editable} onSave={(key, value, label) => update({ [key]: value }, `Changed ${label}`)} />
          </Card>

          <div className="two">
            {item.type !== 'nda' ? (
              <Card title={`${item.type === 'claim' ? 'Requests & revisions' : 'Responses & revisions'}${rounds ? ` · ${rounds}` : ''}`}>
                <Responses
                  ref={resp}
                  itemId={item.id}
                  siteId={item.site_id}
                  responses={responses.rows}
                  orgs={oMap}
                  allOrgs={orgs.rows}
                  people={pMap}
                  defaultFrom={item.onus_org_id && item.onus_org_id !== swiId ? item.onus_org_id : undefined}
                  defaultOnus="keep"
                  finalLabel={fin ? `Final — move to ${fin.label}` : null}
                  requests={item.type === 'claim' || item.type === 'variation'}
                  canEdit={editable}
                  onSaved={afterResponse}
                  onFiles={() => void files.reload()}
                />
              </Card>
            ) : null}
            <div ref={fuBox}>
              <Card title={`Follow-ups${followUps.rows.filter((f) => !f.done_at).length ? ` · ${followUps.rows.filter((f) => !f.done_at).length}` : ''}`}>
                <FollowUps
                  itemId={item.id}
                  list={followUps.rows}
                  people={pMap}
                  staff={staffList}
                  orgs={oMap}
                  canAdd={canComment}
                  canEditAll={editable}
                  me={me}
                  onChanged={() => void followUps.reload()}
                />
              </Card>
            </div>
          </div>

          <div className="two">
            {internal ? (
              <div ref={mailBox}>
                <Card title={`Related emails${emails.rows.length ? ` · ${emails.rows.length}` : ''}`}>
                  <Emails itemId={item.id} list={emails.rows} canEdit={editable} onChanged={() => void emails.reload()} />
                </Card>
              </div>
            ) : null}
            <div ref={fileBox}>
              <Card title={`Files${files.rows.length ? ` · ${new Set(files.rows.map((f) => f.doc_key)).size}` : ''}`}>
                <ItemFiles files={files.rows} siteId={item.site_id} itemId={item.id} canEdit={editable && can('files', 4)} orgs={orgs.rows} onChanged={() => void files.reload()} />
              </Card>
            </div>
          </div>

          {restData.length ? (
            <section className="card">
              <button type="button" className={`histh ${moreOpen ? 'open' : ''}`} aria-expanded={moreOpen} onClick={() => setMoreOpen(!moreOpen)}>
                <svg className="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" />
                </svg>
                <span className="k">More details from Smartsheet</span>
                <span className="muted small">{restData.length} fields</span>
              </button>
              {moreOpen ? (
                <div className="card-body">
                  <dl className="facts">
                    {restData.map(([k, v]) => (
                      <div key={k}>
                        <dt>{k}</dt>
                        <dd>{showValue(v)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ) : null}
            </section>
          ) : null}

          <History itemId={item.id} names={names} version={version} />

          <p className="muted small">
            {item.ss_row_id ? `Imported from Smartsheet${item.ss_created_by ? ` · first entered by ${item.ss_created_by}` : ''}${item.ss_created_at ? ` on ${fmtDateTime(item.ss_created_at)}` : ''}. ` : ''}
            Last changed {fmtDateTime(item.updated_at)}.
          </p>
        </div>

        <aside className="item-rail" aria-label="Comments">
          <div className="rail-head">
            <span className="rail-title">
              <TypeTag type={item.type} /> Comments{nComments ? ` · ${nComments}` : ''}
            </span>
            <span className="muted small">Everyone who can see this item sees comments, unless marked SWI only.</span>
          </div>
          <div className="rail-body">
            <Comments itemId={item.id} siteId={item.site_id} canComment={canComment} files={files.rows} onFilesChanged={() => void files.reload()} onCount={setNComments} />
          </div>
        </aside>
      </div>

      <RecordDrawer
        open={edit}
        onClose={() => setEdit(false)}
        title={`Edit ${item.number ?? def.one}`}
        table="items"
        fields={itemFields(def, buildings.rows, orgs.rows)}
        record={item}
        canEdit={editable}
        onSaved={() => void load()}
        label={(r) => `${def.one} ${String(r.number ?? '')}`}
      />
    </div>
  );
}

